/**
 * rd-expiry W2.3 — Frames 2–4 catch-after overlay (presentational).
 *
 * The honest catch-after surface: the reservation EXPIRED (the slot was released).
 * One calm screen offers to grab it back. Driven by `phase`:
 *  - 'prompt' (Frame 2): "Your reservation ran out" → "Get my time back".
 *  - 'reheld' (Frame 3): "Got it back" → auto-resume (no button; the guard resumes).
 *  - 'taken'  (Frame 4): "That time was just booked" → "Pick a new time".
 *
 * CSS + SVGs + copy are copied verbatim from the confirmed mockup
 * (session-recovery-states.html, Frames 2–4); copy says "reservation," never "hold".
 * A recover-hold failure surfaces as an inline retry (NOT an eviction).
 */

import styles from './CatchAfterOverlay.module.css';

export type CatchAfterPhase = 'prompt' | 'reheld' | 'taken';

export interface CatchAfterOverlayProps {
  phase: CatchAfterPhase;
  /** Formatted reservation date/time for the "When" chip (Frame 2). null → hidden. */
  whenText: string | null;
  /** True while recover-hold is in flight — disables the action button. */
  recovering: boolean;
  /** Inline retryable error copy (recover-hold failed). null → none. */
  errorMessage: string | null;
  /** Frame 2 "Get my time back" → recover-hold. */
  onRecover: () => void;
  /** Frame 4 "Pick a new time" → cancel + navigate to the detail page. */
  onRepick: () => void;
}

export function CatchAfterOverlay({
  phase,
  whenText,
  recovering,
  errorMessage,
  onRecover,
  onRepick,
}: CatchAfterOverlayProps) {
  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="ca-title"
      aria-describedby="ca-sub"
      data-testid="catch-after"
    >
      {phase === 'reheld' ? (
        <RehealdContent />
      ) : phase === 'taken' ? (
        <TakenContent recovering={recovering} onRepick={onRepick} />
      ) : (
        <PromptContent
          whenText={whenText}
          recovering={recovering}
          errorMessage={errorMessage}
          onRecover={onRecover}
        />
      )}
    </div>
  );
}

/** Frame 2 — the reservation expired → "Get my time back". */
function PromptContent({
  whenText,
  recovering,
  errorMessage,
  onRecover,
}: Pick<CatchAfterOverlayProps, 'whenText' | 'recovering' | 'errorMessage' | 'onRecover'>) {
  return (
    <>
      <div className={`${styles.ovIcon} ${styles.warm}`} aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8v4l3 2" />
        </svg>
      </div>
      <h2 id="ca-title" className={styles.ovTitle}>Your reservation ran out</h2>
      <p id="ca-sub" className={styles.ovSub}>
        Your reservation ran out while you were away. Want it back? If it&apos;s still open, it&apos;s
        yours — otherwise we&apos;ll find you a new time.
      </p>
      {whenText && (
        <span className={styles.ovWhen}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
          {whenText}
        </span>
      )}
      <div className={styles.rcActions}>
        <button type="button" className={styles.rcBtn} onClick={onRecover} disabled={recovering}>
          {recovering ? 'Getting it back…' : 'Get my time back'}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="12 5 19 12 12 19" />
          </svg>
        </button>
      </div>
      {errorMessage && (
        <p className={styles.ovError} role="alert">
          {errorMessage}
        </p>
      )}
    </>
  );
}

/** Frame 3 — re-held → brief confirmation, then the guard auto-resumes. */
function RehealdContent() {
  return (
    <>
      <div className={`${styles.ovIcon} ${styles.ok}`} aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
      <h2 id="ca-title" className={styles.ovTitle}>Got it back</h2>
      <p id="ca-sub" className={styles.ovSub}>
        Your time is reserved again — <b>15 more minutes</b> to finish.
      </p>
      <div className={styles.ovFoot}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M21 12a9 9 0 1 1-6.2-8.56" />
        </svg>
        Picking up where you left off…
      </div>
    </>
  );
}

/** Frame 4 — taken → "Pick a new time". */
function TakenContent({
  recovering,
  onRepick,
}: Pick<CatchAfterOverlayProps, 'recovering' | 'onRepick'>) {
  return (
    <>
      <div className={`${styles.ovIcon} ${styles.warm}`} aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
          <path d="M9.5 16.5l5-5M14.5 16.5l-5-5" />
        </svg>
      </div>
      <h2 id="ca-title" className={styles.ovTitle}>That time was just booked</h2>
      <p id="ca-sub" className={styles.ovSub}>
        Someone grabbed that time while you were away. Let&apos;s find you another time —{' '}
        <b>nothing was charged</b>.
      </p>
      <div className={styles.rcActions}>
        <button type="button" className={styles.rcBtn} onClick={onRepick} disabled={recovering}>
          Pick a new time
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="12 5 19 12 12 19" />
          </svg>
        </button>
      </div>
    </>
  );
}
